import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// React Router solo admite UN bloqueador de navegacion a la vez, y con varios usa el
// ultimo registrado y descarta los demas. Por eso los formularios no bloquean: se
// anotan aqui, y un solo componente en la raiz pregunta si hay algo sin guardar.
const UnsavedChangesContext = createContext(null);

const SIN_PROVIDER = {
  haySucios: false,
  haySuciosAhora: () => false,
  registrarSucio: () => {},
  quitarSucio: () => {},
};

export function UnsavedChangesProvider({ children }) {
  // La verdad vive en el ref, y se actualiza en el mismo instante en que alguien se
  // anota o se da de baja. El estado es solo para pintar: llega un commit despues, y
  // ese retraso importa porque guardar y navegar pasan en el MISMO gesto -- si el
  // bloqueador leyera el estado, preguntaria "¿Descartar cambios?" encima de un
  // guardado exitoso.
  const suciosRef = useRef(new Set());
  const [sucios, setSucios] = useState(() => new Set());

  const registrarSucio = useCallback((id) => {
    if (suciosRef.current.has(id)) return;
    suciosRef.current.add(id);
    setSucios(new Set(suciosRef.current));
  }, []);

  const quitarSucio = useCallback((id) => {
    if (!suciosRef.current.has(id)) return;
    suciosRef.current.delete(id);
    setSucios(new Set(suciosRef.current));
  }, []);

  // Se consulta en el momento de navegar, no un commit antes. Es estable, asi que el
  // bloqueador de la raiz se registra una sola vez.
  const haySuciosAhora = useCallback(() => suciosRef.current.size > 0, []);

  const valor = useMemo(
    () => ({ haySucios: sucios.size > 0, haySuciosAhora, registrarSucio, quitarSucio }),
    [sucios, haySuciosAhora, registrarSucio, quitarSucio],
  );

  return (
    <UnsavedChangesContext.Provider value={valor}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

// Sin provider devuelve un doble inerte: un formulario montado suelto (una prueba, por
// ejemplo) tiene que seguir funcionando igual que antes de que esto existiera.
export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext) || SIN_PROVIDER;
}
